import {gql} from '@apollo/client';
import React from 'react';

import {requestAccess} from 'auth/RequestAccess';
import DataConnector from 'connect/DataConnector';
import {Container, HydratedContainerTransferInfo} from 'constants/SampleConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FreezeThawInfo} from 'samples/ContainerDetailsPane';
import {ExpirationInfo} from 'samples/containers/ContainerExpirationDate';

import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import * as StorableActions from '../StorableActions';
import * as StorableAuth from '../storables/StorableAuth';
import StorableStore from '../StorableStore';
import {ContainerViewQuery, ContainerViewQueryVariables} from './__generated/ContainerViewQuery';
import ContainerView from './ContainerView';

export const EXPIRATION_INFO_FRAGMENT = gql`
  fragment ExpirationInfoFragment on ExpirationInfoNode {
    id
    isExpired
    isOverridden
    isTrackingEnabled
    inheritedExpirationDate
    expirationDate
    computedFromEntity {
      ... on RegistryItemInterface {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const FREEZE_THAW_INFO_FRAGMENT = gql`
  fragment FreezeThawInfoFragment on FreezeThawInfoNode {
    id
    isTrackingEnabled
    freezeThawCount
  }
`;

// TODO(BNCH-93708): Pull in only the necessary information for the component
export const CONTAINER_VIEW_QUERY = gql`
  query ContainerViewQuery($id: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    container(id: $id) {
      ... on ContainerNode {
        ...ContainerGraphQL
        parentStorable(skipAuthCheck: true) {
          ... on StorableInterface {
            ...StorableSummaryGraphQL
          }
          ...InaccessibleInfoGraphQL
        }
        expirationInfo {
          ...ExpirationInfoFragment
        }
        freezeThawInfo {
          ...FreezeThawInfoFragment
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('Container').fragment}
  ${getFragmentWrapper('StorableSummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${EXPIRATION_INFO_FRAGMENT}
  ${FREEZE_THAW_INFO_FRAGMENT}
`;

class ContainerViewContainerConnector extends DataConnector<ContainerViewContainerProps, EnhancedProps> {
  override stores = [StorableStore];

  enhanceProps(): EnhancedProps {
    return {
      authErrorMessage: this.storableStore().getAuthErrorMessage(this.props.containerId),
      containerTransferInfo: this.storableStore().getContainerTransferInfo(this.props.containerId),
    };
  }
}

export interface ContainerViewContainerProps {
  containerId: string;
  showFullView?: boolean;
  isWritableOverride?: boolean;
}
interface EnhancedProps {
  authErrorMessage?: string;
  containerTransferInfo: HydratedContainerTransferInfo | null;
}

type QueryingProps = ContainerViewContainerProps & EnhancedProps;

interface Props extends QueryingProps {
  container: Container | null;
  parentStorable: StorableSummaryGraphQL | null;
  expirationInfo: ExpirationInfo | null;
  freezeThawInfo: FreezeThawInfo | null;
  reload?: () => void;
}

export function QueryingContainerViewContainer(props: QueryingProps): JSX.Element {
  const queryResult = useSkippableBenchlingQuery<ContainerViewQuery, ContainerViewQueryVariables>(
    CONTAINER_VIEW_QUERY,
    {
      variables: {id: props.containerId},
      skip: Boolean(props.authErrorMessage),
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  let container = null;
  let parentStorable = null;
  let expirationInfo = null;
  let freezeThawInfo = null;
  if (queryResult.type === QueryResultType.SUCCESS) {
    if (!isInaccessible(queryResult.data.container)) {
      // TODO(BNCH-93708): Remove need to convert to REST by updating types in component and subcomponents
      container = graphQLToREST('Container', queryResult.data.container);
      const parentStorableGQL = queryResult.data.container.parentStorable;
      parentStorable = parentStorableGQL && !isInaccessible(parentStorableGQL) ? parentStorableGQL : null;
      // TODO(BNCH-93708): Just pass these through the container type directly once no longer using REST types
      expirationInfo = queryResult.data.container.expirationInfo;
      freezeThawInfo = queryResult.data.container.freezeThawInfo;
    } else if (queryResult.data.container.canRequestAccess) {
      requestAccess(queryResult.data.container);
      FileBrowserActions.removeFromWorkspace(props.containerId);
    }
  }

  return (
    <ContainerViewContainer
      {...props}
      container={container}
      parentStorable={parentStorable}
      expirationInfo={expirationInfo}
      freezeThawInfo={freezeThawInfo}
      reload={queryResult.refetch}
    />
  );
}

export class ContainerViewContainer extends React.Component<Props> {
  static defaultProps = {showFullView: true};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override componentDidUpdate(prevProps: Props, prevState: any): void {
    if (
      (prevProps.container == null && this.props.container != null) ||
      (prevProps.containerTransferInfo && !this.props.containerTransferInfo)
    ) {
      StorableActions.fetchContainerTransferInfo(this.props.containerId);
    }
  }

  override render(): JSX.Element {
    const {container} = this.props;
    const isWritable = container?.permissions.writable ?? false;
    const isUnarchivable = container?.permissions.unarchivable ?? false;
    if (this.props.authErrorMessage) {
      return <StorableAuth.ErrorBanner message={this.props.authErrorMessage} />;
    }
    return (
      <ContainerView
        container={container}
        containerTransferInfo={this.props.containerTransferInfo}
        isWritable={this.props.isWritableOverride != null ? this.props.isWritableOverride : isWritable}
        isReadOnlyOverride={
          this.props.isWritableOverride != null ? !this.props.isWritableOverride : undefined
        }
        isUnarchivable={isUnarchivable}
        parentStorable={this.props.parentStorable}
        expirationInfo={this.props.expirationInfo}
        freezeThawInfo={this.props.freezeThawInfo}
        showFullView={this.props.showFullView}
        reload={this.props.reload}
      />
    );
  }
}

export default ContainerViewContainerConnector.connect(QueryingContainerViewContainer);
